import type { MaybeComputedElementRef } from '@vueuse/core';
import type { EffectScope, InjectionKey, MaybeRef, ShallowRef } from 'vue';
import { tryOnScopeDispose, unrefElement, useMutationObserver } from '@vueuse/core';
import { Viewer } from 'cesium';
import { computed, getCurrentScope, markRaw, provide, shallowReadonly, shallowRef, toRaw, toValue, watchEffect } from 'vue';

/**
 * @internal
 */
export const CREATE_VIEWER_INJECTION_KEY: InjectionKey<Readonly<ShallowRef<Viewer | undefined>>> = Symbol('CREATE_VIEWER_INJECTION_KEY');

/**
 * @internal
 */
export const CREATE_VIEWER_COLLECTION = new WeakMap<EffectScope, Readonly<ShallowRef<Viewer | undefined>>>();

/**
 * Pass in an existing Viewer instance,
 * which can be accessed by the current component and its descendant components using {@link useViewer}
 *
 * When the Viewer instance referenced by this overloaded function becomes invalid, it will not trigger destruction.
 * @param viewer - Existing viewer instance
 * @returns The Viewer instance
 */
export function createViewer(
  viewer: MaybeRef<Viewer | undefined>,
): Readonly<ShallowRef<Viewer | undefined>>;

/**
 * Initialize a Viewer instance, which can be accessed by the
 * current component and its descendant components using {@link useViewer}.
 *
 * The Viewer instance created by this overloaded function will automatically be destroyed when it becomes invalid.
 *
 * @param element - The DOM element or ID that will contain the widget
 * @param options - see `Viewer.ConstructorOptions`
 * @returns The Viewer instance
 */
export function createViewer(
  element: MaybeComputedElementRef,
  options?: Viewer.ConstructorOptions,
): Readonly<ShallowRef<Viewer | undefined>>;

/**
 * @internal
 */
export function createViewer(
  arg1?: MaybeRef<Viewer | undefined> | MaybeComputedElementRef,
  arg2?: Viewer.ConstructorOptions,
): Readonly<ShallowRef<Viewer | undefined>> {
  const viewer = shallowRef<Viewer>();
  const readonlyViewer = shallowReadonly(viewer);

  provide(CREATE_VIEWER_INJECTION_KEY, readonlyViewer);

  const scope = getCurrentScope();
  if (scope) {
    CREATE_VIEWER_COLLECTION.set(scope, readonlyViewer);
  }

  const canvas = computed(() => viewer.value?.canvas);
  const body = typeof document !== 'undefined' ? document.body : undefined;

  // Watch for the canvas being removed from the DOM
  if (body) {
    useMutationObserver(body, () => {
      if (canvas.value && !body.contains(canvas.value)) {
        viewer.value = undefined;
      }
    }, {
      childList: true,
      subtree: true,
    });
  }

  watchEffect((onCleanup) => {
    const value = toRaw(toValue(arg1));
    if (value instanceof Viewer) {
      viewer.value = markRaw(value);
    }
    else if (value) {
      const element = unrefElement(value);
      viewer.value = new Viewer(element, arg2);
      onCleanup(() => !viewer.value?.isDestroyed() && viewer.value?.destroy());
    }
    else {
      viewer.value = undefined;
    }
  });

  tryOnScopeDispose(() => {
    viewer.value = undefined;
  });

  return computed(() => {
    return viewer.value?.isDestroyed() ? undefined : viewer.value;
  });
}
